import { useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { Link, useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useAuthStore } from "@/stores/auth-store"
import { ApiClientError } from "@/lib/api-client"
import { Button } from "@/components/ui/button"

const registerSchema = z
  .object({
    company_name: z.string().min(2),
    first_name: z.string().min(1),
    last_name: z.string().min(1),
    email: z.string().email(),
    password: z.string().min(8),
    password_confirmation: z.string().min(8),
  })
  .refine((data) => data.password === data.password_confirmation, {
    message: "Hasła muszą być identyczne",
    path: ["password_confirmation"],
  })

type RegisterForm = z.infer<typeof registerSchema>

export function RegisterPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { register: registerUser } = useAuthStore()
  const [error, setError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RegisterForm>({
    resolver: zodResolver(registerSchema),
  })

  const onSubmit = async (data: RegisterForm) => {
    try {
      setError(null)
      await registerUser({
        email: data.email,
        password: data.password,
        first_name: data.first_name,
        last_name: data.last_name,
        company_name: data.company_name,
      })
      navigate("/dashboard")
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message)
      } else {
        setError("Wystąpił nieoczekiwany błąd")
      }
    }
  }

  const inputBorder = (field: keyof RegisterForm) =>
    errors[field] ? "border-red-500" : "border-input"

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <h2 className="page-title">
        {t("auth.register")}
      </h2>

      {error && (
        <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-950/30 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="space-y-1.5">
        <label className="text-sm font-medium text-muted-foreground">
          {t("auth.companyName")}
        </label>
        <input
          {...register("company_name")}
          className={`h-9 w-full rounded-md border bg-transparent px-3 text-sm text-foreground outline-none ${inputBorder("company_name")}`}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-muted-foreground">
            {t("auth.firstName")}
          </label>
          <input
            {...register("first_name")}
            className={`h-9 w-full rounded-md border bg-transparent px-3 text-sm text-foreground outline-none ${inputBorder("first_name")}`}
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-muted-foreground">
            {t("auth.lastName")}
          </label>
          <input
            {...register("last_name")}
            className={`h-9 w-full rounded-md border bg-transparent px-3 text-sm text-foreground outline-none ${inputBorder("last_name")}`}
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <label className="text-sm font-medium text-muted-foreground">
          {t("auth.email")}
        </label>
        <input
          type="email"
          autoComplete="email"
          {...register("email")}
          className={`h-9 w-full rounded-md border bg-transparent px-3 text-sm text-foreground outline-none ${inputBorder("email")}`}
        />
      </div>

      <div className="space-y-1.5">
        <label className="text-sm font-medium text-muted-foreground">
          {t("auth.password")}
        </label>
        <input
          type="password"
          autoComplete="new-password"
          {...register("password")}
          className={`h-9 w-full rounded-md border bg-transparent px-3 text-sm text-foreground outline-none ${inputBorder("password")}`}
        />
      </div>

      <div className="space-y-1.5">
        <label className="text-sm font-medium text-muted-foreground">
          {t("auth.confirmPassword")}
        </label>
        <input
          type="password"
          autoComplete="new-password"
          {...register("password_confirmation")}
          className={`h-9 w-full rounded-md border bg-transparent px-3 text-sm text-foreground outline-none ${inputBorder("password_confirmation")}`}
        />
        {errors.password_confirmation && (
          <p className="text-xs text-red-500">
            {errors.password_confirmation.message}
          </p>
        )}
      </div>

      <Button type="submit" disabled={isSubmitting} className="w-full">
        {isSubmitting ? t("common.loading") : t("auth.register")}
      </Button>

      <p className="text-center text-sm text-muted-foreground">
        {t("auth.hasAccount")}{" "}
        <Link to="/login" className="text-primary hover:underline">
          {t("auth.login")}
        </Link>
      </p>
    </form>
  )
}
